/* eslint-disable import/no-cycle */
import i18n from 'i18next';
import { css, html, nothing } from 'lit';
import { ifDefined } from 'lit-html/directives/if-defined.js';
import { property, query } from 'lit/decorators.js';
import { BaseElement } from '../../components/base-element/base-element';
import { customElement } from '../../components/base-element/decorators/customElement';
import { TreeItemWC } from '../../components/tree-item/tree-item.wc';
import { isOrgInMyOrgs } from '../utils/org-favorites';
import { Org, OrgWithChildren } from './types';

import '../../components/avatar/avatar.wc';
import '../../components/buttons/icon-button/icon-button.wc';
import '../../components/icon/icon.wc';

@customElement('ps-universal-nav-tree-child')
export class ChildTreeItemWC extends BaseElement {
  static styles = css`
    :host {
      display: contents;
    }
  `;

  @property({ type: Object }) child: OrgWithChildren | Org;

  @property({ type: String }) orgId: string | undefined;

  @property({ type: Boolean }) isDisabled: boolean = false;

  /** Ids of the orgs the user has marked as favorites ("My Orgs"). */
  @property({ type: Array }) favoriteOrgIds: string[] = [];

  /** Id of the primary/top org — always in My Orgs and its star is read-only. */
  @property({ type: String }) primaryOrgId: string | undefined;

  @query('ps-tree-item') treeItem: TreeItemWC;

  createRenderRoot() {
    return this;
  }

  private handleFavoriteToggle(event: Event) {
    event.stopPropagation();

    if (!this.child.id || this.isDisabled) return;

    this.emit('favorite-toggle', { detail: { orgId: this.child.id } });
  }

  /**
   * Without this, Enter/Space on the focused star never activates it: the
   * keydown is composed and bubbles up into the ancestor `ps-tree`, whose
   * own `handleKeyDown` calls `event.preventDefault()` unconditionally for
   * both keys BEFORE it even looks for which `ps-tree-item` has focus — and
   * since focus is on this nested `<ps-icon-button>`, not on the
   * `ps-tree-item` roving-tabindex target itself, `ps-tree` finds no
   * `activeItem` and does nothing with the (already prevented) key. The
   * native Enter/Space→click activation on the star's own inner `<button>`
   * is cancelled by that `preventDefault()` before it can fire (PSTUDIO-3330
   * review round 2, confirmed by reading `tree.wc.ts`'s `handleKeyDown`
   * directly). Stopping propagation here — mirroring the existing
   * `@click` stopPropagation above — keeps every key the star itself
   * doesn't care about from ever reaching `ps-tree` while it has focus,
   * which is the same "a focused descendant control owns its own keys"
   * contract a roving-tabindex container is expected to honor.
   */
  // An instance method on purpose, mirroring `handleFavoriteToggle` above as
  // the star's own event contract, even though this one body doesn't happen
  // to read `this`.
  // eslint-disable-next-line class-methods-use-this
  private handleStarKeyDown(event: KeyboardEvent) {
    event.stopPropagation();
  }

  private renderStar(isTopOrg: boolean, favorited: boolean) {
    // Pinned to the row's right edge regardless of name length — `.c-tree-item__item`
    // (the slotted content's containing block in the flat tree) is `position: relative`,
    // so this resolves correctly across the shadow boundary without touching that
    // shared component's layout for every other consumer.
    const pinRight =
      'position: absolute; inset-inline-end: 6px; top: 50%; transform: translateY(-50%);';

    // The primary/top org shows no star at all in this menu (design ref
    // PSTUDIO-3330) — it's always "in My Orgs" implicitly, with nothing to
    // toggle, so there's nothing to indicate here.
    if (isTopOrg) {
      return nothing;
    }

    // Favorited: always visible, brand purple (design ref PSTUDIO-3330) — `ps-icon-button`
    // has no `color` prop, so the icon's `currentColor` is driven via the same
    // `--ps-icon-button--color` custom property `icon-button.scss` already reads;
    // #8573e7 is `color(purple, 300)` — no JS-side token export exists for it.
    // Not favorited: hidden until the row is hovered/focused, then purple
    // with a SOLID outline — unlike the Roles table's matching hover state,
    // which is dashed — via the CSS variable `tree-item.scss` sets on
    // `.c-tree-item__item`, inheriting across the shadow boundary like any
    // custom property, no JS hover listener needed.
    // `pressed` also makes `ps-icon-button`'s own `::after` pseudo-element visible
    // (its `.c-icon-btn--selected` rule), painted with `--ps-icon-button--active-bg-color`
    // (gray-100 by default) — a filled square behind the star that the star-only
    // look does not want; force it transparent.
    // `.c-icon-btn--tertiary` itself defaults to `opacity: 0.5` at rest (only
    // 0.6 on hover, 1 on active) — a ghost-button treatment that is wrong for
    // an already-favorited star, which must read at full strength always.
    // `--ps-icon-button--opacity` (not plain `opacity` — that would land on
    // this HOST element, not the inner `<button>` inside its own shadow root,
    // which is what `.c-icon-btn--tertiary` actually targets; a host-level
    // `opacity` only compounds with the inner 0.5 instead of overriding it)
    // is the custom property `icon-button.scss` now reads at rest AND on
    // hover/active, so one override wins everywhere instead of only masking
    // the base state.
    // `--ps-icon-button--opacity: 1` on BOTH branches, not only `favorited`:
    // `.c-icon-btn--tertiary` still carries its own ghost-button opacity
    // (0.5 rest / 0.6 hover) underneath this host's `opacity` (the hide/
    // reveal mechanism for the not-favorited branch) — two different
    // properties on two different nodes, which COMPOUND rather than one
    // overriding the other (same trap the `favorited` branch's own comment
    // already names). Without this, a "revealed" not-favorited star hover
    // rendered at host-opacity(1) × inner-hover-opacity(0.6) ≈ 60% strength
    // instead of the full-strength purple the design calls for (PSTUDIO-3330
    // review round 2, role 9).
    const style = favorited
      ? `${pinRight} --ps-icon-button--opacity: 1; --ps-icon-button--color: #8573e7; --ps-icon-button--hover-color: #8573e7; --ps-icon-button--active-bg-color: transparent;`
      : `${pinRight} opacity: var(--ps-tree-item-suffix-opacity, 1); --ps-icon-button--opacity: 1; --ps-icon-button--color: var(--ps-tree-item-suffix-color); --ps-icon-button--hover-color: var(--ps-tree-item-suffix-color);`;

    return html`<ps-icon-button
      name=${favorited ? 'star-filled' : 'star'}
      size="xsmall"
      variant="tertiary"
      ?disabled=${this.isDisabled}
      .pressed=${favorited}
      style=${style}
      .label=${favorited
        ? i18n.t('navigation:orgMenu.removeFromMyOrgs') || 'Remove from My Orgs'
        : i18n.t('navigation:orgMenu.addToMyOrgs') || 'Add to My Orgs'}
      @click=${(event: Event) => this.handleFavoriteToggle(event)}
      @keydown=${(event: KeyboardEvent) => this.handleStarKeyDown(event)}
    ></ps-icon-button>`;
  }

  render() {
    const childImage = this.child.pictureFile?.url;
    const isTopOrg = !!this.child.id && this.child.id === this.primaryOrgId;
    const favorited = isOrgInMyOrgs(
      this.child,
      this.favoriteOrgIds,
      this.primaryOrgId
    );
    const dimmed = !favorited;
    const dimStyle = dimmed ? 'opacity: 0.5;' : '';
    // Reserves room for the star, which is pinned absolute to the row's right
    // edge (see renderStar) — without it a long name's text runs under the icon.
    const nameStyle = `${dimStyle} padding-inline-end: 24px;`;

    const defaultOrgImage = html`<ps-avatar
      size="msmall"
      label=${`${this.child.name?.split(' ')[0]}`}
      shape="square"
      slot="prefix"
      style=${dimStyle}
    ></ps-avatar>`;

    return html`
      <ps-tree-item
        level=${ifDefined(this.child.depth)}
        ?selected=${this.child.id === this.orgId}
        data-org-id=${ifDefined(this.child.id)}
        @click=${() => !this.isDisabled && this.emit('menu-item-selected')}
        ?disabled=${this.isDisabled}
        expanded
      >
        ${childImage
          ? html`<ps-image
              width="20"
              height="20"
              src=${childImage}
              radius="small"
              slot="prefix"
              style=${dimStyle}
            >
              <slot slot="fallbackImage"> ${defaultOrgImage} </slot>
            </ps-image>`
          : defaultOrgImage}
        <span style=${nameStyle}>${this.child.name}</span>
        ${this.renderStar(isTopOrg, favorited)}
        ${(this.child.children || []).map(
          (item: OrgWithChildren | Org) =>
            html`<ps-universal-nav-tree-child
              .child=${item}
              slot="children"
              orgId=${ifDefined(this.orgId)}
              ?isDisabled=${this.isDisabled}
              .favoriteOrgIds=${this.favoriteOrgIds}
              primaryOrgId=${ifDefined(this.primaryOrgId)}
            ></ps-universal-nav-tree-child>`
        )}
      </ps-tree-item>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ps-universal-nav-tree-child': ChildTreeItemWC;
  }
  enum PSElementTagNameMap {
    'ps-universal-nav-tree-child' = 'ps-universal-nav-tree-child',
  }
}
